import React, { useEffect, useState, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLoginModal } from '../components/GoogleLoginModal';
type DecodeContent = {
  precision: string[];
  pattern: string[];
  playout: string[];
  mask: string[];
  genius: string[];
  mirror: string[];
  reframe: string[];
  inevitable: string[];
};
// Stagger fade-in
function useFadeIn(delay: number) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return visible;
}
const FadeBlock = ({
  children,
  delay,
  className = ''
}: {
  children: React.ReactNode;
  delay: number;
  className?: string;
}) => {
  const visible = useFadeIn(delay);
  return <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'} ${className}`} data-id="element-2965">
      {children}
    </div>;
};
// Section label
const SectionLabel = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em] mb-8" data-id="element-2966">
    {children}
  </p>;
// Thin divider
const Divider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`h-px bg-[#E0DDD8] w-full ${className}`} data-id="element-2967" />;
export function PersonalDecode() {
  const navigate = useNavigate();
  const [content, setContent] = useState<DecodeContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [brutalCopied, setBrutalCopied] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'download' | null>(null);
  useEffect(() => {
    const dateOfBirth = sessionStorage.getItem('decodeDateOfBirth');
    const placeOfBirth = sessionStorage.getItem('decodePlaceOfBirth');
    if (!dateOfBirth || !placeOfBirth) {
      navigate('/decode/details');
      return;
    }
    const path = sessionStorage.getItem('decodePath');
    const input = sessionStorage.getItem('decodeInput') || '';
    const storedName = sessionStorage.getItem('decodeName') || '';
    setName(storedName);
    setDob(dateOfBirth);
    const generatedContent = generatePersonalDecode(path, input, storedName);
    setContent(generatedContent);
    setTimeout(() => setLoading(false), 800);
  }, [navigate]);
  const handleSaveClick = () => {
    setPendingAction('save');
    setShowLoginModal(true);
  };
  const handleDownloadClick = () => {
    setPendingAction('download');
    setShowLoginModal(true);
  };
  const handleLogin = () => {
    setShowLoginModal(false);
    if (pendingAction === 'save') navigate('/decode/save');else if (pendingAction === 'download') console.log('PDF download triggered after auth');
    setPendingAction(null);
  };
  if (loading || !content) {
    return <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center" data-id="element-2968">
        <p className="text-[#888888] text-xs font-mono tracking-widest uppercase" data-id="element-2969">
          Calibrating structure...
        </p>
      </div>;
  }
  return <>
      <Helmet data-id="element-2970">
        <title data-id="element-2971">Personal Decode | Lazy Monk Labs</title>
        <meta name="robots" content="noindex" data-id="element-2972" />
      </Helmet>

      <div className="min-h-screen bg-[#F8F7F4] text-[#444444] selection:bg-[#B89C5D]/20" data-id="element-2973">
        {/* MINIMAL HEADER — logo only */}
        <header className="fixed top-0 left-0 right-0 z-50 px-6 py-5 bg-[#F8F7F4]/95 backdrop-blur-sm" data-id="element-2974">
          <Link to="/" className="flex items-center gap-2 w-fit hover:opacity-70 transition-opacity" data-id="element-2975">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs" className="h-6 w-auto" data-id="element-2976" />
          </Link>
        </header>

        <main className="pt-28 pb-40 px-6 sm:px-8" data-id="element-2977">
          <div className="max-w-[680px] mx-auto" data-id="element-2978">
            {/* ENTRY MOMENT */}
            <FadeBlock delay={100} className="mb-24" data-id="element-2979">
              <div className="space-y-3" data-id="element-2980">
                <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-2981">
                  🧬 Personal Decode
                </p>
                {(name || dob) && <p className="text-xs text-[#BBBBBB] font-mono" data-id="element-2982">
                    {name && <span data-id="element-2983">{name}</span>}
                    {name && dob && <span className="mx-2" data-id="element-2984">·</span>}
                    {dob && <span data-id="element-2985">{dob}</span>}
                  </p>}
              </div>
            </FadeBlock>

            {/* L1 MARKER */}
            <FadeBlock delay={200} className="mb-20" data-id="element-2986">
              <p className="text-[10px] font-mono text-[#B89C5D] uppercase tracking-[0.2em]" data-id="element-2987">
                🔵 L1 — Deep Legacy Decoder™
              </p>
            </FadeBlock>

            {/* ❤️ HEART ANCHOR */}
            <FadeBlock delay={400} className="mb-24" data-id="element-2988">
              <SectionLabel data-id="element-2989">❤ Heart Anchor — Core Formation</SectionLabel>

              <p className="text-sm text-[#888888] font-light leading-relaxed mb-10" data-id="element-2990">
                {name ? `${name}'s` : 'Your'} core emotional formation was built
                around:
              </p>

              <div className="space-y-4 mb-12" data-id="element-2991">
                <p className="text-2xl sm:text-3xl text-[#111111] font-medium leading-[1.2]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-2992">
                  Responsibility before freedom.
                </p>
                <p className="text-2xl sm:text-3xl text-[#111111] font-medium leading-[1.2]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-2993">
                  Clarity before expression.
                </p>
              </div>

              <div className="space-y-4 mb-12" data-id="element-2994">
                {content.pattern.map((line, i) => <p key={i} className="text-base text-[#555555] font-light leading-[1.75]" data-id="element-2995">
                    {line}
                  </p>)}
              </div>

              <p className="text-lg sm:text-xl text-[#555555] italic leading-relaxed text-center px-4" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-2996">
                "If I don't think this through properly, it could cost me."
              </p>
            </FadeBlock>

            <Divider className="mb-24" data-id="element-2997" />

            {/* GENIUS + SHADOW */}
            <FadeBlock delay={600} className="mb-24" data-id="element-2998">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-px bg-[#E0DDD8]" data-id="element-2999">
                <div className="bg-[#F8F7F4] p-8 md:p-10 space-y-5" data-id="element-3000">
                  <SectionLabel data-id="element-3001">Genius Mode</SectionLabel>
                  <div className="space-y-4" data-id="element-3002">
                    {content.genius.map((line, i) => <p key={i} className="text-base text-[#444444] font-light leading-[1.75]" data-id="element-3003">
                        {line}
                      </p>)}
                  </div>
                </div>

                <div className="bg-[#EDEAE5] p-8 md:p-10 space-y-5" data-id="element-3004">
                  <SectionLabel data-id="element-3005">Shadow Mode</SectionLabel>
                  <div className="space-y-4" data-id="element-3006">
                    {content.mirror.map((line, i) => <p key={i} className="text-base text-[#3A3A3A] font-light leading-[1.75]" data-id="element-3007">
                        {line}
                      </p>)}
                    <p className="text-base text-[#3A3A3A] font-light leading-[1.75]" data-id="element-3008">
                      You exhaust yourself holding things together that others
                      have already let go.
                    </p>
                    <p className="text-base text-[#3A3A3A] font-light leading-[1.75]" data-id="element-3009">
                      You over-prepare for conversations that never happen.
                    </p>
                  </div>
                </div>
              </div>
            </FadeBlock>

            {/* NERVOUS SYSTEM CODING */}
            <FadeBlock delay={700} className="mb-24" data-id="element-3010">
              <div className="bg-[#F2EFE9] px-8 py-10 sm:px-10 sm:py-12" data-id="element-3011">
                <SectionLabel data-id="element-3012">Nervous System Coding</SectionLabel>
                <div className="space-y-3 text-sm font-mono text-[#555555] leading-loose" data-id="element-3013">
                  <p data-id="element-3014">Threat = uncertainty</p>
                  <p data-id="element-3015">Response = analysis</p>
                  <p data-id="element-3016">Safety = control</p>
                  <p data-id="element-3017">Rest = earned, not given</p>
                  <p data-id="element-3018">Vulnerability = structural risk</p>
                </div>
                <p className="text-sm text-[#888888] font-light mt-8 leading-relaxed" data-id="element-3019">
                  Your system learned early: if you can understand it, you can
                  survive it. That wiring is still running.
                </p>
              </div>
            </FadeBlock>

            {/* THE ROLE YOU TOOK ON */}
            <FadeBlock delay={800} className="mb-24" data-id="element-3020">
              <SectionLabel data-id="element-3021">The Role You Took On</SectionLabel>
              <div className="space-y-5 text-base text-[#444444] font-light leading-[1.85] max-w-[600px]" data-id="element-3022">
                <p data-id="element-3023">
                  Somewhere early, you became the one who held things together.
                  Not because you were asked to. Because you saw that no one
                  else would.
                </p>
                <p data-id="element-3024">
                  You learned that your value was tied to your function. That
                  being needed was the closest thing to being safe.
                </p>
                <p data-id="element-3025">
                  So you got very good at being needed. And very bad at being
                  still.
                </p>
                <p data-id="element-3026">
                  The role became the identity. And now you can't tell where the
                  role ends and you begin.
                </p>
              </div>
            </FadeBlock>

            <Divider className="mb-24" data-id="element-3027" />

            {/* WHERE YOU WITHHOLD */}
            <FadeBlock delay={900} className="mb-24" data-id="element-3028">
              <SectionLabel data-id="element-3029">Where You Withhold</SectionLabel>
              <div className="space-y-3 text-base text-[#444444] font-light leading-[1.75]" data-id="element-3030">
                <p data-id="element-3031">You don't ask for help. You ask for information.</p>
                <p data-id="element-3032">
                  You don't say "I'm struggling." You say "I'm figuring it out."
                </p>
                <p data-id="element-3033">You don't show uncertainty. You show a plan.</p>
                <p data-id="element-3034">You don't let people see the cost. Only the output.</p>
              </div>
            </FadeBlock>

            {/* RELATIONSHIP PATTERN */}
            <FadeBlock delay={1000} className="mb-24" data-id="element-3035">
              <SectionLabel data-id="element-3036">Relationship Pattern</SectionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 mb-10" data-id="element-3037">
                <div className="space-y-4" data-id="element-3038">
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.15em] mb-5" data-id="element-3039">
                    You in relationship
                  </p>
                  <div className="space-y-3 text-base text-[#444444] font-light leading-[1.75]" data-id="element-3040">
                    <p data-id="element-3041">You become the structural anchor.</p>
                    <p data-id="element-3042">You hold the complexity others can't carry.</p>
                    <p data-id="element-3043">You offer clarity before you offer presence.</p>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-3044">
                  <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.15em] mb-5" data-id="element-3045">
                    Partner experience
                  </p>
                  <div className="space-y-3 text-base text-[#555555] font-light leading-[1.75]" data-id="element-3046">
                    <p data-id="element-3047">They feel supported but not met.</p>
                    <p data-id="element-3048">They sense you're always slightly elsewhere.</p>
                    <p data-id="element-3049">
                      They can't reach the part of you that needs something.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#888888] italic text-center" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-3050">
                You rarely feel held yourself. You have normalized being the one
                who understands, not the one who is understood.
              </p>
            </FadeBlock>

            {/* CURRENT LIFE CHAPTER */}
            <FadeBlock delay={1050} className="mb-24" data-id="element-3051">
              <SectionLabel data-id="element-3052">Current Life Chapter</SectionLabel>
              <div className="space-y-3 text-base text-[#444444] font-light leading-[1.75]" data-id="element-3053">
                <p data-id="element-3054">
                  The structures that carried you here are starting to feel
                  insufficient.
                </p>
                <p data-id="element-3055">Not because they failed.</p>
                <p data-id="element-3056">Because you outgrew them.</p>
                <p data-id="element-3057">This is not a crisis.</p>
                <p data-id="element-3058">It is a transition between operating systems.</p>
              </div>
            </FadeBlock>

            <Divider className="mb-24" data-id="element-3059" />

            {/* POWER LEAK */}
            <FadeBlock delay={1100} className="mb-24" data-id="element-3060">
              <div className="bg-[#EDEAE5] px-8 py-10 sm:px-10 sm:py-12" data-id="element-3061">
                <SectionLabel data-id="element-3062">Power Leak</SectionLabel>
                <div className="space-y-3 text-base text-[#3A3A3A] font-light leading-[1.75] mb-8" data-id="element-3063">
                  <p data-id="element-3064">You spend energy managing how you appear.</p>
                  <p data-id="element-3065">You spend energy anticipating what others need.</p>
                  <p data-id="element-3066">You spend energy suppressing what you actually feel.</p>
                  <p data-id="element-3067">
                    You spend energy holding open loops that others have closed.
                  </p>
                </div>
                <p className="text-sm text-[#666666] font-light leading-relaxed pt-6 border-t border-[#D8D5D0]" data-id="element-3068">
                  The leak isn't dramatic. It's constant. And it's costing you
                  more than you know.
                </p>
              </div>
            </FadeBlock>

            {/* IF NOTHING CHANGES */}
            <FadeBlock delay={1150} className="mb-24" data-id="element-3069">
              <SectionLabel data-id="element-3070">If Nothing Changes</SectionLabel>
              <div className="space-y-3 text-base text-[#444444] font-light leading-[1.75] mb-12" data-id="element-3071">
                <p data-id="element-3072">Left unchecked: high competence, low aliveness.</p>
                <p data-id="element-3073">You will have built something impressive.</p>
                <p data-id="element-3074">And you will feel strangely empty inside it.</p>
                <p data-id="element-3075">Not because you chose wrong.</p>
                <p data-id="element-3076">
                  Because you never stopped to ask what you actually wanted.
                </p>
              </div>

              <p className="text-lg sm:text-xl text-[#555555] italic leading-relaxed text-center px-4" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-3077">
                "I built everything I said I would. I just didn't expect it to
                feel like this."
              </p>
            </FadeBlock>

            <Divider className="mb-24" data-id="element-3078" />

            {/* 🔥 L2 — BRUTAL DIAGNOSTIC MIRROR */}
            <FadeBlock delay={1200} className="mb-24" data-id="element-3079">
              <p className="text-[10px] font-mono text-[#B89C5D] uppercase tracking-[0.2em] mb-16" data-id="element-3080">
                🔥 L2 — Brutal Diagnostic Mirror
              </p>

              {/* Real Core Pattern */}
              <div className="mb-16 space-y-5" data-id="element-3081">
                <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-3082">
                  🪓 Real Core Pattern
                </p>
                <p className="text-base text-[#555555] font-light leading-relaxed" data-id="element-3083">
                  {content.precision[0]}
                </p>
                <div className="space-y-2 text-base text-[#3A3A3A] font-light leading-[1.75]" data-id="element-3084">
                  {content.precision.slice(1).map((line, i) => <p key={i} data-id="element-3085">{line}</p>)}
                </div>
              </div>

              {/* Hidden Fear */}
              <div className="mb-16 space-y-5" data-id="element-3086">
                <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-3087">
                  🪓 Hidden Fear
                </p>
                <div className="pl-5 border-l border-[#B89C5D]/30 italic text-[#555555] text-base leading-relaxed mb-5" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-3088">
                  "If I stop holding this together, everything falls apart."
                </div>
                <p className="text-base text-[#3A3A3A] font-light leading-[1.75]" data-id="element-3089">
                  Your deepest fear is not failure. It is irrelevance. The
                  terror that without your function, you have no place.
                </p>
              </div>

              {/* Relationship Risk */}
              <div className="mb-16 space-y-5" data-id="element-3090">
                <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-3091">
                  🪓 Relationship Risk
                </p>
                <div className="space-y-3 text-base text-[#3A3A3A] font-light leading-[1.75]" data-id="element-3092">
                  {content.mask.map((line, i) => <p key={i} data-id="element-3093">{line}</p>)}
                </div>
              </div>

              {/* Loop under pressure */}
              <div className="mb-20 py-12 border-t border-b border-[#E0DDD8]" data-id="element-3094">
                <SectionLabel data-id="element-3095">Under Pressure</SectionLabel>
                <div className="flex flex-col items-center space-y-2 font-mono text-xs text-[#444444] tracking-wider" data-id="element-3096">
                  <span data-id="element-3097">Uncertainty</span>
                  <span className="text-[#B89C5D]" data-id="element-3098">↓</span>
                  <span data-id="element-3099">Analysis</span>
                  <span className="text-[#B89C5D]" data-id="element-3100">↓</span>
                  <span data-id="element-3101">Delay</span>
                  <span className="text-[#B89C5D]" data-id="element-3102">↓</span>
                  <span data-id="element-3103">More analysis</span>
                  <span className="text-[#B89C5D]" data-id="element-3104">↓</span>
                  <span className="text-[#888888]" data-id="element-3105">Even more pressure</span>
                </div>
              </div>

              {/* 🧩 Final Mirror */}
              <div className="space-y-8" data-id="element-3106">
                <p className="text-[10px] font-mono text-[#AAAAAA] uppercase tracking-[0.2em]" data-id="element-3107">
                  🧩 Final Mirror
                </p>
                <p className="text-2xl sm:text-3xl text-[#111111] leading-[1.3]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-3108">
                  {content.reframe[0]}
                </p>
                <p className="text-base text-[#555555] font-light leading-[1.75]" data-id="element-3109">
                  {content.reframe[1]}
                </p>
              </div>
            </FadeBlock>

            {/* SHARE — subtle, after L2 */}
            <FadeBlock delay={1250} className="mb-16" data-id="element-3110">
              <div className="py-8 border-t border-[#E0DDD8]" data-id="element-3111">
                <p className="text-sm text-[#888888] italic text-center mb-5" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-3112">
                  If this felt uncomfortably accurate, you already know who else
                  needs to read it.
                </p>
                <div className="flex justify-center" data-id="element-3113">
                  <button onClick={() => {
                  const text = content.reframe.slice(0, 2).join('\n\n') + '\n\n— lazymonklabs.com/decode/details';
                  navigator.clipboard.writeText(text);
                  setBrutalCopied(true);
                  setTimeout(() => setBrutalCopied(false), 2000);
                }} className="text-[10px] font-mono text-[#AAAAAA] hover:text-[#555555] transition-colors uppercase tracking-[0.2em]" data-id="element-3114">
                    {brutalCopied ? '✓ Copied' : 'Copy this line'}
                  </button>
                </div>
              </div>
            </FadeBlock>

            {/* POST-READ ACTION */}
            <FadeBlock delay={1300} data-id="element-3115">
              <div className="pt-16 border-t border-[#E0DDD8]" data-id="element-3116">
                <p className="text-xs text-[#AAAAAA] font-light text-center mb-12 leading-relaxed font-mono uppercase tracking-[0.15em]" data-id="element-3117">
                  This decode is yours.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-3118">
                  <button onClick={handleDownloadClick} className="flex items-center gap-3 text-sm text-[#444444] hover:text-[#111111] transition-colors border border-[#CCCCCC] hover:border-[#888888] px-8 py-4" data-id="element-3119">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-3120">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" data-id="element-3121" />
                    </svg>
                    <span className="text-xs uppercase tracking-wider" data-id="element-3122">
                      Download PDF
                    </span>
                  </button>

                  <button onClick={handleSaveClick} className="flex items-center gap-3 text-sm text-[#888888] hover:text-[#444444] transition-colors" data-id="element-3123">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-3124">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" data-id="element-3125" />
                    </svg>
                    <span className="text-xs uppercase tracking-wider" data-id="element-3126">
                      Save to account
                    </span>
                  </button>
                </div>

                <p className="text-[10px] font-mono text-[#CCCCCC] text-center mt-8 uppercase tracking-[0.15em]" data-id="element-3127">
                  Nothing is stored unless you choose to save it.
                </p>
              </div>
            </FadeBlock>
          </div>
        </main>
      </div>

      <GoogleLoginModal isOpen={showLoginModal} onClose={() => {
      setShowLoginModal(false);
      setPendingAction(null);
    }} onLogin={handleLogin} data-id="element-3128" />
    </>;
}
// CONTENT GENERATION LOGIC — UNCHANGED
function generatePersonalDecode(path: string | null, input: string, name: string): DecodeContent {
  const isSituationPath = path === 'situation' && input.length > 0;
  const words = input.toLowerCase();
  const hasWork = words.includes('work') || words.includes('job') || words.includes('career');
  const hasRelationship = words.includes('relationship') || words.includes('partner') || words.includes('friend');
  const hasMeaning = words.includes('meaning') || words.includes('purpose') || words.includes('lost');
  let precision: string[];
  if (isSituationPath) {
    if (hasWork) {
      precision = [`You described a friction around work and output. You likely interpret this as a problem of strategy or placement—that if you just found the right role or method, the tension would resolve.`, `That is incorrect. The friction isn't in the job. It's in the way you structure your own value.`, `You are trying to solve a structural issue with logistical moves. That never works.`];
    } else if (hasRelationship) {
      precision = [`You described a tension in how you relate to others. You likely see this as a communication issue or a mismatch of needs.`, `That is a surface reading. The actual friction is in how you maintain your own boundaries while seeking connection.`, `You are trying to negotiate safety when you should be establishing structure.`];
    } else {
      precision = [`You described a situation where things feel off. You likely attribute this to external timing or a temporary lack of clarity.`, `That is a defense mechanism. The friction is internal and structural.`, `You are waiting for the environment to stabilize so you can feel secure. That is the wrong order of operations.`];
    }
  } else {
    precision = [`Even when things are operating well, there is a subtle tension you tend to ignore. It appears as a low-level hum of dissatisfaction, even after a win.`, `You likely dismiss this as "just how it is" or the cost of high performance.`, `It is neither. It is a signal that your operating system is misaligned with your output. You have normalized a friction that is not necessary.`];
  }
  let pattern: string[];
  let playout: string[];
  let mask: string[];
  let genius: string[];
  let mirror: string[];
  let reframe: string[];
  if (hasWork || !hasRelationship && !hasMeaning) {
    pattern = [`You are wired to impose order on chaos. Your default setting is to assess, categorize, and restructure.`, `This is not a learned skill; it is a biological imperative. When you enter a room, you don't just see what is there; you see how it should function.`, `You move through uncertainty by building frameworks. If you cannot map it, you cannot trust it.`];
    playout = [`This explains why you often feel responsible for things that are not yours to carry. You see the structural flaw, so you assume the burden of fixing it.`, `It creates a cycle where you over-function to compensate for others' lack of clarity. You become the load-bearing wall in every room.`, `The cost is that you rarely experience true rest. Even your downtime is structured.`];
    mask = [`Externally, you appear competent, unshakeable, and decisive. People bring you their chaos because they know you will metabolize it.`, `Internally, you often feel a deep fatigue and a resentment that you have to be the one who holds it all together.`, `The friction comes from the gap between your public capability and your private exhaustion.`];
    genius = [`You possess an unusual ability to see systems where others see noise. You can diagnose the core bug in a complex situation almost instantly.`, `The cost is that you struggle to turn this lens off. You analyze relationships, leisure, and yourself with the same ruthless efficiency.`];
    mirror = [`The pattern stops working when you try to engineer human emotion. You cannot optimize intimacy. You cannot project-manage connection.`, `When you try, you become rigid and controlling, alienating the very people you are trying to support.`];
    reframe = [`You do not need to stop building. You need to stop using your structure as a shield against vulnerability.`, `The maturity of this pattern is not in building stronger walls, but in building gates that you actually open.`];
  } else if (hasRelationship) {
    pattern = [`You are wired to track the emotional baseline of any environment. Your default setting is to scan for dissonance and restore harmony.`, `This is not just empathy; it is a surveillance system. You know how everyone in the room is feeling before they do.`, `You move through uncertainty by establishing consensus. If we are not aligned, we are not safe.`];
    playout = [`This explains why you often lose your own signal in the presence of strong personalities. You auto-tune to the loudest frequency in the room.`, `It creates a cycle where you over-accommodate to avoid conflict, only to resent the compromise later.`, `The cost is that your own desires often feel blurry or secondary to the needs of the group.`];
    mask = [`Externally, you appear easygoing, adaptable, and supportive. People feel seen by you.`, `Internally, you often feel invisible and drained by the constant calculation of others' states.`, `The friction comes from the gap between your public warmth and your private depletion.`];
    genius = [`You possess an unusual ability to synthesize opposing viewpoints and create cohesion. You are the glue in any system.`, `The cost is that you often sacrifice your own structural integrity to maintain the bond.`];
    mirror = [`The pattern stops working when you confuse peace with silence. You will swallow your own truth to keep the surface calm.`, `This is not harmony; it is dishonesty. It rots the foundation of the relationships you are trying to protect.`];
    reframe = [`You do not need to stop feeling. You need to stop being responsible for everyone else's regulation.`, `The maturity of this pattern is realizing that true connection requires the friction of two distinct individuals, not the merging of one into the other.`];
  } else {
    pattern = [`You are wired to seek the underlying truth of things. Your default setting is to question the surface narrative and dig for the core.`, `This is not intellectual curiosity; it is an existential drive. You cannot operate on autopilot.`, `You move through uncertainty by seeking significance. If it doesn't mean something, you cannot sustain it.`];
    playout = [`This explains why you often feel alienated in standard environments. You are looking for depth where others are content with function.`, `It creates a cycle where you withdraw to preserve your integrity, then feel isolated by your own withdrawal.`, `The cost is that you often struggle to engage with the mundane requirements of life.`];
    mask = [`Externally, you appear intense, thoughtful, perhaps a bit distant. People respect your depth but may find you hard to access.`, `Internally, you often feel a sense of urgency—that you are running out of time to find the thing that matters.`, `The friction comes from the gap between the depth you perceive and the reality you inhabit.`];
    genius = [`You possess an unusual ability to articulate what is actually happening beneath the surface. You are a truth-teller.`, `The cost is that you can become addicted to the intensity of the reveal, burning down structures that just needed maintenance.`];
    mirror = [`The pattern stops working when you use "authenticity" as an excuse for disengagement. You judge the world for being shallow because you are afraid to play the game.`, `You cannot deconstruct everything. Eventually, you have to live in something.`];
    reframe = [`You do not need to lower your standards. You need to lower your defense.`, `The maturity of this pattern is finding meaning in the maintenance, not just the breakthrough.`];
  }
  const inevitable = [`Seeing this pattern once helps. It names the thing you have felt but could not articulate.`, `But seeing it across your entire life—how it shaped your childhood, your career choices, your relationships—changes how you live.`, `A Life Decode maps the entire architecture of your existence, showing you not just who you are, but why you are here.`];
  return {
    precision,
    pattern,
    playout,
    mask,
    genius,
    mirror,
    reframe,
    inevitable
  };
}